import { createSelector } from '@reduxjs/toolkit'

import { queryView, type QueryResultLike } from '@krp/client-runtime'

import { ORIGIN_LABELS } from '../../catalog-links/model/selectors'
import { commonPhotoThumbUrl } from '../../common-photos/api/urls'
import { COMMON_CATALOG_PAGE_SIZE } from './commonCatalogSlice'
import type {
  CommonCatalogIntentState,
  CommonCatalogPage,
  CommonCatalogRowVm,
  CommonCatalogVm,
  CommonProduct,
} from './types'

export interface CommonCatalogVmInput {
  intent: CommonCatalogIntentState
  /**
   * Страница из кэша. Пока идёт следующая, binding отдаёт прежнюю (`data` хука
   * RTK Query): таблица не мигает «Загрузкой» на каждой странице.
   */
  result: QueryResultLike<CommonCatalogPage>
  /** Вид ресурса словами — от реестра форм пакета вида, а не ветвлением здесь. */
  kindTitle: (kind: string) => string
}

const NO_ROWS: readonly CommonCatalogRowVm[] = []

function rowOf(product: CommonProduct, kindTitle: (kind: string) => string): CommonCatalogRowVm {
  const first = product.identities[0]
  return {
    id: product.id,
    name: product.displayName,
    sharedName: product.displayName === product.name ? null : product.name,
    sku: product.sku,
    unitCode: product.unitCode,
    barcodeCount: product.barcodeCount,
    sources: product.identities.map((identity) => ({
      resourceId: identity.resourceId,
      name: identity.resourceName,
      kindTitle: kindTitle(identity.resourceKind),
      remoteKey: identity.remoteKey,
      originText: ORIGIN_LABELS[identity.origin] ?? identity.origin,
    })),
    hiddenCount: product.hiddenIdentities,
    linked: product.identities.length > 0 || product.hiddenIdentities > 0,
    linksResourceId: first === undefined ? null : first.resourceId,
    refs: product.identities.map((identity) => ({
      resourceId: identity.resourceId,
      resourceKind: identity.resourceKind,
      remoteKey: identity.remoteKey,
      state: null,
    })),
    commonPhotoUrl: commonPhotoThumbUrl(product.id, product.mainPhoto),
  }
}

/** Фабрика: один экземпляр на смонтированный binding (D07). */
export const makeSelectCommonCatalogVm = () => {
  const selectQuery = createSelector([(input: CommonCatalogVmInput) => input.result], (result) =>
    queryView(result, (data) => data.items.length === 0),
  )
  const selectRows = createSelector(
    [(input: CommonCatalogVmInput) => input.result.data, (input: CommonCatalogVmInput) => input.kindTitle],
    (data, kindTitle): readonly CommonCatalogRowVm[] =>
      data === undefined || data.items.length === 0 ? NO_ROWS : data.items.map((product) => rowOf(product, kindTitle)),
  )
  return createSelector(
    [selectQuery, selectRows, (input: CommonCatalogVmInput) => input.result.data, (input: CommonCatalogVmInput) => input.intent],
    (query, rows, data, intent): CommonCatalogVm => {
      const total = data?.total ?? 0
      const pageSize = data?.pageSize ?? COMMON_CATALOG_PAGE_SIZE
      const pages = Math.max(1, Math.ceil(total / pageSize))
      // Номер страницы — намерение человека: пока идёт следующая, он уже показан.
      const page = intent.page
      const from = total === 0 ? 0 : Math.min(total, (page - 1) * pageSize + 1)
      const to = Math.min(total, from + rows.length - 1)
      const search = intent.query.trim()
      return {
        query,
        search: intent.query,
        rows,
        total,
        page,
        pages,
        canPrevious: page > 1,
        canNext: page < pages,
        rangeText: total === 0 ? 'Товаров нет' : `${from}–${Math.max(from, to)} из ${total}`,
        emptyText:
          search === ''
            ? 'В общем каталоге пока нет товаров: они появляются, когда карточки ресурсов связывают в «Связях товаров» или публикуют общий товар'
            : `По запросу «${search}» товаров нет`,
      }
    },
  )
}
